import { Pipeline } from './pipeline';
import { Activity, Criteria, InactiveStatusType } from './activity';
import { Metamodel } from '../base/model';
import { ISwitchCase } from '../../interfaces/pipeline/ISwitchCase';
import { IModel } from '../base/annotation';

const untilActivityType = 'Until';
const forEachActivityType = 'ForEach';
const ifActivityType = 'IfCondition';
const switchActivityType = 'Switch';

export const containerActivityModelNames = [untilActivityType, forEachActivityType, ifActivityType, switchActivityType];

export function getAllActivityNamesWithinPipeline(pipeline: Pipeline): string[] {
    if (!pipeline) {
        return [];
    }

    const activityNames: string[] = [];
    pipeline.activities.forEach((activity: Activity) => {
        activityNames.push(activity.name);
        const activityType = Metamodel.modelOf(activity).name;
        // TODO: we should use a more specific type for each of the activityTypes but importing that class would create a cyclic dependency at the moment.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const activityAsAny = activity as any;
        if (activityType === untilActivityType || activityType === forEachActivityType) {
            activityNames.push(...getAllActivityNamesWithinPipeline(activityAsAny.pipeline));
        } else if (activityType === ifActivityType) {
            activityNames.push(...getAllActivityNamesWithinPipeline(activityAsAny.ifTrueActivityPipeline));
            activityNames.push(...getAllActivityNamesWithinPipeline(activityAsAny.ifFalseActivityPipeline));
        } else if (activityType === switchActivityType) {
            activityNames.push(...getAllActivityNamesWithinPipeline(activityAsAny.defaultPipeline));
            (activityAsAny.cases as ISwitchCase[]).forEach((switchCase) => activityNames.push(...getAllActivityNamesWithinPipeline(switchCase.casePipeline)));
        }
    });
    return activityNames;
}

export function countActivitiesInPipeline(pipeline: Pipeline, activityModelToCheck?: IModel): number {
    let activitiesCount: number = 0;
    if (pipeline) {
        pipeline.activities.filter((a) => !activityModelToCheck || Metamodel.modelOf(a).name === activityModelToCheck.name).forEach((activity) => {
            activitiesCount++;
            const activityType = Metamodel.modelOf(activity).name;
            // TODO: we should use a more specific type for each of the activityTypes but importing that class would create a cyclic dependency at the moment.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const activityAsAny = activity as any;
            if ((activityType === untilActivityType || activityType === forEachActivityType) && activityAsAny.pipeline) {
                activitiesCount += countActivitiesInPipeline(activityAsAny.pipeline, activityModelToCheck);
            } else if (activityType === ifActivityType) {
                activitiesCount += countActivitiesInPipeline(activityAsAny.ifTrueActivityPipeline, activityModelToCheck);
                activitiesCount += countActivitiesInPipeline(activityAsAny.ifFalseActivityPipeline, activityModelToCheck);
            } else if (activityType === switchActivityType) {
                activitiesCount += countActivitiesInPipeline(activityAsAny.defaultPipeline, activityModelToCheck);
                (activityAsAny.cases as ISwitchCase[]).forEach((switchCase) => activitiesCount += countActivitiesInPipeline(switchCase.casePipeline, activityModelToCheck));
            }
        });
    }
    return activitiesCount;
}

export function countActivitiesOfTypeInPipeline(pipeline: Pipeline, activityModelToCheck: IModel): number {
    let activitiesCount: number = 0;
    if (pipeline) {
        pipeline.activities.forEach((activity) => {
            const activityType = Metamodel.modelOf(activity).name;
            if (activityType === activityModelToCheck.name) {
                activitiesCount++;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const activityAsAny = activity as any;
            if ((activityType === untilActivityType || activityType === forEachActivityType) && activityAsAny.pipeline) {
                activitiesCount += countActivitiesInPipeline(activityAsAny.pipeline, activityModelToCheck);
            } else if (activityType === ifActivityType) {
                activitiesCount += countActivitiesInPipeline(activityAsAny.ifTrueActivityPipeline, activityModelToCheck);
                activitiesCount += countActivitiesInPipeline(activityAsAny.ifFalseActivityPipeline, activityModelToCheck);
            } else if (activityType === switchActivityType) {
                activitiesCount += countActivitiesInPipeline(activityAsAny.defaultPipeline, activityModelToCheck);
                (activityAsAny.cases as ISwitchCase[]).forEach((switchCase) => activitiesCount += countActivitiesInPipeline(switchCase.casePipeline, activityModelToCheck));
            }
        });
    }
    return activitiesCount;
}


export function pipelineContainsContainerActivity(pipeline: Pipeline): boolean {
    const act = pipeline.activities.find((activity) => containerActivityModelNames.includes(Metamodel.modelOf(activity).name));

    return !!act;
}

export function getRootPipelineObjectId(pipeline: Pipeline): string {
    if (!pipeline || !pipeline.rootOwner) {
        return '';
    }
    const root = pipeline.rootOwner as Pipeline;
    return root.objectId ?? '';
}

// NOTE: anredd - only traverses downwards. Be mindful of which pipeline is passed in as parameter
export function pipelineHasActivity(pipeline: Pipeline, activityModelToCheck: IModel): boolean {
    for (const activity of pipeline.activities) {
        const activityType = Metamodel.modelOf(activity).name;
        if (activityType === activityModelToCheck.name && activity.state !== 'Inactive') {
            return true;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const activityAsAny = activity as any;
        if (activityType === untilActivityType || activityType === forEachActivityType) {
            if (pipelineHasActivity(activityAsAny.pipeline, activityModelToCheck)) {
                return true;
            }
        } else if (activityType === ifActivityType) {
            if (pipelineHasActivity(activityAsAny.ifTrueActivityPipeline, activityModelToCheck)) {
                return true;
            }
            if (pipelineHasActivity(activityAsAny.ifFalseActivityPipeline, activityModelToCheck)) {
                return true;
            }
        } else if (activityType === switchActivityType) {
            if (pipelineHasActivity(activityAsAny.defaultPipeline, activityModelToCheck)) {
                return true;
            }
            for (const switchCase of activityAsAny.cases as ISwitchCase[]) {
                if (pipelineHasActivity(switchCase.casePipeline, activityModelToCheck)) {
                    return true;
                }
            }
        }
    }
    return false;
}

export function containerActivityHasActivity(containerActivity: Activity, activityModelToCheck: IModel): boolean {
    const containerModelName = Metamodel.modelOf(containerActivity).name;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const containerActivityAsAny = containerActivity as any;
    if (containerModelName === untilActivityType || containerModelName === forEachActivityType) {
        if (pipelineHasActivity(containerActivityAsAny.pipeline, activityModelToCheck)) {
            return true;
        }
    } else if (containerModelName === ifActivityType) {
        if (pipelineHasActivity(containerActivityAsAny.ifTrueActivityPipeline, activityModelToCheck)) {
            return true;
        }
        if (pipelineHasActivity(containerActivityAsAny.ifFalseActivityPipeline, activityModelToCheck)) {
            return true;
        }
    } else if (containerModelName === switchActivityType) {
        if (pipelineHasActivity(containerActivityAsAny.defaultPipeline, activityModelToCheck)) {
            return true;
        }
        for (const switchCase of containerActivityAsAny.cases as ISwitchCase[]) {
            if (pipelineHasActivity(switchCase.casePipeline, activityModelToCheck)) {
                return true;
            }
        }
    }
    return false;
}

export function getPipelineActivitiesByType(pipelineJson: string, activityTypes: string[]): string {
    const pipelineObject = JSON.parse(pipelineJson);

    const activities =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pipelineObject.properties.activities.filter((activity: any) => (activityTypes.includes(activity.type)));

    // Nesting rules: [ForEach'/'Until'] could nest only ['IfCondition'/'Switch'], ['IfCondition'/'Switch'] can't nest any
    // Level 0 nested activities under pipeline layers. Nesting level 0 possibles: [ForEach'/'Until'/'IfCondition'/'Switch']
    const containerActivities =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pipelineObject.properties.activities.filter((activity: any) => (containerActivityModelNames.includes(activity.type)));

    // Level 1 nested activities under level 0 activities. Nesting level 1 possibles: [IfCondition'/'Switch']
    if (containerActivities) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        containerActivities.forEach((activity: any) => {
            // Loops only could have nested conditions
            if (activity.type === forEachActivityType || activity.type === untilActivityType) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (activity.typeProperties?.activities?.filter((activity: any) => (activity.type === ifActivityType || activity.type === switchActivityType)))?.forEach((actv: any) => {
                    containerActivities.push(actv);
                });
            }
        });

        // Now check all activies under nested activities
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        containerActivities?.forEach((activity: any) => {
            if (activity.type === switchActivityType) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (activity.typeProperties?.defaultActivities?.filter((activity: any) => (activityTypes.includes(activity.type))))?.forEach((actv: any) => {
                    activities.push(actv);
                });
                // Cases are kind of nested so need to handle separately
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                activity.typeProperties?.cases?.forEach((caseValue: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (caseValue?.activities?.filter((activity: any) => (activityTypes.includes(activity.type))))?.forEach((actv: any) => {
                        activities.push(actv);
                    });
                });
            } else if (activity.type === ifActivityType) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (activity.typeProperties?.ifTrueActivities?.filter((activity: any) => (activityTypes.includes(activity.type))))?.forEach((actv: any) => {
                    activities.push(actv);
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (activity.typeProperties?.ifFalseActivities?.filter((activity: any) => (activityTypes.includes(activity.type))))?.forEach((actv: any) => {
                    activities.push(actv);
                });
            } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (activity.typeProperties?.activities?.filter((activity: any) => (activityTypes.includes(activity.type))))?.forEach((actv: any) => {
                        activities.push(actv);
                    });
            }
        });
    }
    return JSON.stringify(activities);
}

export function getInactiveCriteria(criteria: Criteria): InactiveStatusType {
    switch (criteria) {
        case Criteria.FAILURE:
            return 'Failed';
        case Criteria.SKIPPED:
            return 'Skipped';
        default:
            return 'Succeeded';
    }
}

export function getDefaultInactivateMarkAsPath(activity: Activity): InactiveStatusType {
    const linkCriterias = [...activity.toLinks].map((link) => link.criteria);
    for (const criteria in Criteria) {
        const criteriaNum = Number(criteria);
        if (!isNaN(criteriaNum) && linkCriterias.includes(criteriaNum)) {
            return getInactiveCriteria(criteriaNum);
        }
    }
    return getInactiveCriteria(Criteria.SUCCESS);
}
